import Tagify from '@yaireo/tagify';
import * as notification from '../../components/notifications';

const FORM_ID = 'job-seeker-profile-visibility';
const INPUT_ID = 'blocked-employers-input';
const ERROR_ID = 'blocked-employers-error';

const MIN_QUERY_LENGTH = 2;
const SUGGESTIONS_DEBOUNCE_MS = 500;
const DEFAULT_MAX_TAGS = 20;
const SUGGESTIONS_LIMIT = 10;

const INVALID_TAG_CLASS = 'jb-blocked-employer-tag--invalid';

/**
 * "Hide my profile from specific employers" card on the job seeker profile page.
 *
 * The card is only rendered when the tenant has the feature on, so this no-ops
 * when the markup is absent.
 */
const initPage = () => {
  const form = document.getElementById(FORM_ID);
  const inputElm = document.getElementById(INPUT_ID);

  if (!form || !inputElm) {
    return;
  }

  const tagify = createTagify(form, inputElm);

  prefillTags(tagify, inputElm);
  bindSuggestions(form, tagify);

  $(form).submit(() => {
    save(form, tagify);
    return false;
  });
};

const createTagify = (form, inputElm) => new Tagify(inputElm, {
  whitelist: [],
  enforceWhitelist: false,
  keepInvalidTags: false,
  dropdown: {
    enabled: 1,
    maxItems: SUGGESTIONS_LIMIT,
    closeOnSelect: false,
  },
  maxTags: parseInt(form.dataset.maxTags, 10) || DEFAULT_MAX_TAGS,
});

const prefillTags = (tagify, inputElm) => {
  try {
    const initialTags = JSON.parse(inputElm.dataset.initialTags || '[]');

    if (Array.isArray(initialTags) && initialTags.length) {
      tagify.addTags(initialTags);
    }
  } catch (e) {
    // Ignore malformed prefill data.
  }
};

const bindSuggestions = (form, tagify) => {
  let source;
  let debounceTimer;

  tagify.on('input', (e) => {
    const value = e && e.detail ? e.detail.value : '';

    tagify.settings.whitelist.length = 0;
    clearTimeout(debounceTimer);
    clearFieldError();

    if (!value || value.trim().length < MIN_QUERY_LENGTH) {
      tagify.loading(false).dropdown.hide.call(tagify);
      return;
    }

    tagify.loading(true).dropdown.hide.call(tagify);

    // One request per pause in typing rather than one per keystroke.
    debounceTimer = setTimeout(() => {
      source && source.cancel();
      source = axios.CancelToken.source();

      axios.get('/api/employers', {
        params: {query: value, limit: SUGGESTIONS_LIMIT},
        cancelToken: source.token,
      }).then((response) => {
        tagify.settings.whitelist = response.data.data || [];
        tagify.loading(false).dropdown.show.call(tagify, value);
      }).catch((error) => {
        if (!axios.isCancel(error)) {
          tagify.loading(false);
          // The lookup failing (e.g. throttled) shouldn't block the seeker —
          // they can still type a full name and add it — but it should be
          // visible, not a silently-empty dropdown.
          showFieldError(form.dataset.searchError);
        }
      });
    }, SUGGESTIONS_DEBOUNCE_MS);
  });
};

const save = async (form, tagify) => {
  clearErrors(tagify);
  tagify.setReadonly(true);
  $('.btn-submit', form).loading();

  const names = tagify.value.map((tag) => tag.value);

  try {
    const response = await axios.put('/api/account/blocked_employers', {
      employer_names: names,
    });

    // The server collapses entries that mean the same company, so render what
    // was actually stored instead of what we sent.
    tagify.removeAllTags();
    tagify.addTags(response.data.data || []);

    notification.success(response.data.message);
  } catch (e) {
    showErrors(form, tagify, e);
  } finally {
    tagify.setReadonly(false);
    $('.btn-submit', form).loading(false);
  }
};

/**
 * Bootstrap's `.invalid-feedback` ships `display: none` by default and only
 * flips to `display: block` for a `.is-invalid` sibling — merely removing
 * `d-none` leaves that base rule in effect and the message stays invisible.
 * `d-block` carries `!important`, so it's what actually reveals the element
 * (see the same pattern in utils/form.js's showErrorsNextToInputs).
 */
const showFieldError = (message) => {
  $('#' + ERROR_ID).text(message || '').removeClass('d-none').addClass('d-block');
};

const clearFieldError = () => {
  $('#' + ERROR_ID).text('').removeClass('d-block').addClass('d-none');
};

const markTagInvalid = (tagElm, message) => {
  tagElm.classList.add(INVALID_TAG_CLASS);
  tagElm.style.setProperty('--tag-bg', 'rgba(211, 148, 148, 0.5)');
  tagElm.setAttribute('title', message);
};

const clearTagInvalid = (tagElm) => {
  tagElm.classList.remove(INVALID_TAG_CLASS);
  tagElm.style.removeProperty('--tag-bg');
  tagElm.removeAttribute('title');
};

const clearErrors = (tagify) => {
  clearFieldError();
  tagify.getTagElms().forEach(clearTagInvalid);
};

/**
 * Validation errors come back keyed by `employer_names.{index}`, which maps
 * straight onto the chips — so mark the offending chip rather than dumping an
 * undirected message under the field.
 */
const showErrors = (form, tagify, error) => {
  const errors = error.response && error.response.data ? error.response.data.errors : null;

  if (!errors) {
    notification.danger(form.dataset.genericError);
    return;
  }

  const tagElms = tagify.getTagElms();
  const messages = [];

  Object.keys(errors).forEach((field) => {
    const index = parseInt(field.split('.')[1], 10);
    const tagElm = Number.isInteger(index) ? tagElms[index] : null;

    errors[field].forEach((message) => {
      if (messages.indexOf(message) === -1) {
        messages.push(message);
      }

      if (tagElm) {
        markTagInvalid(tagElm, message);
      }
    });
  });

  if (messages.length) {
    showFieldError(messages.join(' '));
  } else {
    notification.danger(form.dataset.genericError);
  }
};

$(initPage);
